import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation, NavLink } from 'react-router-dom';
import { 
  SquarePen, MessageSquare, FolderOpen, MoreHorizontal, Trash2, PenLine, 
  ChevronDown, ChevronRight, Check, X, LayoutDashboard, Lightbulb, 
  Bookmark, FileText, FilePlus, Image, Brain, Building2, SlidersHorizontal,
  User, BarChart3, Crown, Settings, LogOut, Globe, CircleUser,
  History, Plus, LucideIcon, Headphones, Mail, Bell, BarChart2, BookOpen, 
  Compass, Bot, TrendingUp, Wrench, PenSquare, Search, PanelLeft, Eye,
  ArrowLeft, ArrowRight
} from 'lucide-react';

import { LCommon, LComponents } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { useLocalizedRoutes } from '@/hooks/useLocalizedRoutes';
import { TYPOGRAPHY, ICONS, INTERACTION_STATES } from '@/design-system';
import { useAuth } from '@/contexts/AuthContext';
import { useAvatar } from '@/contexts/AvatarContext';
import { useLocale } from '@/contexts/LocaleContext';
import { useGuestExitPreventionOptional } from '@/contexts/GuestExitPreventionContext';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarHeader, SidebarFooter, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarMenuSub, SidebarMenuSubItem, SidebarMenuSubButton, useSidebar
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useChatSessions, type GroupedSessions } from '@/hooks/useChatSessions';
import { useChatProjects } from '@/hooks/useChatProjects';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { useUserRole } from '@/hooks/useUserRole';
import { useOpportunityNotifications } from '@/hooks/useOpportunityNotifications';
import { CircularProgress } from './CircularProgress';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/constants';
import { routeTo } from '@/lib/routeHelpers';
import { isReturningVisitor, getSmartAuthRoute } from '@/utils/authRedirect';
import { db } from '@/lib/supabaseQuery';
import { RESPONSIVE } from '@/design-system/helpers/responsive';

const SIDEBAR_MENU_STATE_KEY = 'unified-sidebar-menu-state';

// HOME section menu items
interface MenuItem {
  key: string;
  title: string;
  localizationId?: string;
  url: string;
  icon: LucideIcon;
  badge?: 'progress' | 'count';
  requireSuperAdmin?: boolean;
  children?: MenuItem[];
}

const homeMenuItems: MenuItem[] = [
  { 
    key: 'overview', 
    title: 'Account', 
    localizationId: 'sidebar.account', 
    url: ROUTES.ACCOUNT, 
    icon: CircleUser, 
    children: [
      { key: 'profile', title: 'Profile', localizationId: 'sidebar.profile', url: ROUTES.ACCOUNT_PROFILE, icon: User },
      { key: 'performance', title: 'Performance', localizationId: 'sidebar.performance', url: ROUTES.ACCOUNT_PERFORMANCE, icon: BarChart3 },
      { key: 'settings', title: 'Preferences', localizationId: 'sidebar.settings', url: ROUTES.ACCOUNT_SETTINGS, icon: SlidersHorizontal },
    ]
  },
  { key: 'opportunities', title: 'Opportunities', localizationId: 'sidebar.opportunities', url: ROUTES.ACCOUNT_OPPORTUNITIES, icon: Lightbulb, badge: 'count' },
  { key: 'favorites', title: 'Favorites', localizationId: 'sidebar.favorites', url: ROUTES.ACCOUNT_FAVORITES, icon: Bookmark },
];

const contentMenuItems: MenuItem[] = [
  { key: 'new_publication', title: 'Publish New', localizationId: 'sidebar.publish_new', url: ROUTES.ACCOUNT_PUBLICATIONS_NEW, icon: FilePlus },
  { key: 'publications', title: 'All Publications', localizationId: 'sidebar.all_publications', url: ROUTES.ACCOUNT_PUBLICATIONS, icon: FileText },
  { key: 'comments', title: 'Comments', localizationId: 'sidebar.comments', url: ROUTES.ACCOUNT_COMMENTS, icon: MessageSquare },
  { key: 'media', title: 'Media', localizationId: 'sidebar.media', url: ROUTES.ACCOUNT_MEDIA, icon: Image },
];

const adminMenuItems: MenuItem[] = [
  {
    key: 'operations', title: 'Operations', localizationId: 'sidebar.operations', url: ROUTES.ADMIN_OPERATIONS, icon: Building2,
    children: [
      { key: 'help_center', title: 'Help Center', localizationId: 'nav.help_center', url: ROUTES.ADMIN_OPERATIONS_HELP_CENTER, icon: Headphones },
      { key: 'indexation', title: 'Indexation', localizationId: 'sidebar.indexation', url: ROUTES.ADMIN_OPERATIONS_INDEXATION, icon: Search },
      { key: 'analytics', title: 'Analytics', localizationId: 'sidebar.analytics', url: ROUTES.ADMIN_OPERATIONS_ANALYTICS, icon: BarChart2 },
    ]
  },
  {
    key: 'intelligence', title: 'Data Intelligence', localizationId: 'sidebar.data_intelligence', url: ROUTES.ADMIN_INTELLIGENCE, icon: Brain,
    children: [
      { key: 'intel_analytics', title: 'Analytics', localizationId: 'sidebar.analytics', url: ROUTES.ADMIN_INTELLIGENCE_ANALYTICS, icon: BarChart3 },
      { key: 'intel_content_review', title: 'Content Review', localizationId: 'sidebar.content_review', url: ROUTES.ADMIN_INTELLIGENCE_CONTENT_REVIEW, icon: FileText },
      { key: 'intel_knowledge', title: 'Knowledge', localizationId: 'sidebar.knowledge', url: ROUTES.ADMIN_INTELLIGENCE_KNOWLEDGE, icon: BookOpen },
      { key: 'intel_strategy', title: 'Strategy', localizationId: 'sidebar.strategy', url: ROUTES.ADMIN_INTELLIGENCE_STRATEGY, icon: Compass },
      { key: 'intel_agents', title: 'AI Agents', localizationId: 'sidebar.ai_agents', url: ROUTES.ADMIN_INTELLIGENCE_AGENTS, icon: Bot },
      { key: 'intel_localization', title: 'Localization', localizationId: 'sidebar.localization', url: ROUTES.ADMIN_MANAGEMENT_LOCALIZATION, icon: Globe },
    ]
  },
  {
    key: 'management', title: 'Management', localizationId: 'sidebar.management', url: ROUTES.ADMIN_MANAGEMENT, icon: SlidersHorizontal,
    children: [
      { key: 'mgmt_overview', title: 'Overview', localizationId: 'sidebar.overview', url: ROUTES.ADMIN_MANAGEMENT_PERFORMANCE, icon: TrendingUp },
      { key: 'mgmt_settings', title: 'Settings', localizationId: 'sidebar.settings', url: ROUTES.ADMIN_MANAGEMENT_SETTINGS, icon: Settings },
      { key: 'mgmt_workflows', title: 'Workflows', localizationId: 'sidebar.workflows', url: ROUTES.ADMIN_MANAGEMENT_WORKFLOWS, icon: Wrench, requireSuperAdmin: true },
      { key: 'mgmt_observer', title: 'Observer', localizationId: 'sidebar.observer', url: ROUTES.ADMIN_MANAGEMENT_OBSERVER, icon: Eye, requireSuperAdmin: true },
    ]
  },
];

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useParams();
  const { user, signOut } = useAuth();
  const { avatarUrl, displayName } = useAvatar();
  const { settings } = useLocale();
  const { getPath } = useLocalizedRoutes();
  const { lCommon } = useLocalizations();
  
  const { state, toggleSidebar, isMobile } = useSidebar();
  // On mobile sheet, always show expanded (with labels) regardless of desktop state
  const isCollapsed = isMobile ? false : state === 'collapsed';
  
  // Permissions
  const { canAccessPublications, canAccessComments, canAccessMedia, canAccessOperations, canAccessIntelligence, canAccessSettings, isSuperAdmin } = useUserRole();
  
  // Profile completion for badge
  const { completionPercentage } = useProfileCompletion();
  
  // Opportunity count for badge
  const { unreadCount } = useOpportunityNotifications();
  
  // Chat data
  const { groupedSessions, deleteSession } = useChatSessions({});
  const { projects, createProject, updateProject, deleteProject } = useChatProjects();
  
  // Menu state
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_MENU_STATE_KEY);
      return saved ? JSON.parse(saved) : { chat: false, projects: false, history: true, overview: true };
    } catch { return { chat: false, projects: false, history: true, overview: true }; }
  });
  
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  
  
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingSessionTitle, setEditingSessionTitle] = useState('');
  const newProjectInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_MENU_STATE_KEY, JSON.stringify(openMenus)); } catch {}
  }, [openMenus]);
  
  useEffect(() => {
    if (isCreatingProject && newProjectInputRef.current) newProjectInputRef.current.focus();
  }, [isCreatingProject]);
  
  const toggleMenu = (key: string) => setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  
  // Guest exit prevention context (optional - only used when active)
  const guestExitPrevention = useGuestExitPreventionOptional();
  
  const handleNewChat = () => {
    // If guest (no user), show modal if active session, otherwise redirect to auth
    if (!user) {
      if (guestExitPrevention?.requestNavigation(getPath('chat'))) {
        return; // Modal shown
      }
      navigate(getSmartAuthRoute());
      return;
    }
    
    const chatPath = getPath('chat');
    if (!sessionId && location.pathname === chatPath) return;
    navigate(chatPath);
  };
  
  const handleSelectSession = (id: string) => navigate(routeTo.chatSession(id));
  
  const handleDeleteSession = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (sessionId === id) navigate(getPath('chat'));
    await deleteSession(id);
  };
  
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) { setIsCreatingProject(false); return; }
    try { await createProject(newProjectName.trim()); setNewProjectName(''); setIsCreatingProject(false); } catch {}
  };
  
  const handleSignOut = async () => { await signOut(); navigate(ROUTES.ROOT); };
  
  const userInitials = user?.email?.substring(0, 2).toUpperCase() || 'U';
  
  const isActive = (url: string) => location.pathname === url || location.pathname.startsWith(url + '/');
  
  
  // Check content permissions
  const canSeeContent = canAccessPublications || canAccessComments || canAccessMedia;
  const canSeeAdmin = canAccessOperations || canAccessIntelligence || canAccessSettings;
  
  // Filter sessions and limit to 5 total across all groups
  const limitedSessions = useMemo(() => {
    const query = chatSearchQuery.toLowerCase().trim();
    const filterFn = (s: GroupedSessions['today'][number]) => 
      !query || s.title?.toLowerCase().includes(query) || false;
    
    // Combine all sessions with their group label
    const allSessions: Array<GroupedSessions['today'][number] & { group: string }> = [
      ...groupedSessions.today.filter(filterFn).map(s => ({ ...s, group: 'Today' })),
      ...groupedSessions.yesterday.filter(filterFn).map(s => ({ ...s, group: 'Yesterday' })),
      ...groupedSessions.lastWeek.filter(filterFn).map(s => ({ ...s, group: 'Last 7 days' })),
      ...groupedSessions.older.filter(filterFn).map(s => ({ ...s, group: 'Older' }))
    ].slice(0, 5); // Limit to top 5
    
    // Group back by label for display
    return allSessions.reduce((acc, session) => {
      if (!acc[session.group]) acc[session.group] = [];
      acc[session.group].push(session);
      return acc;
    }, {} as Record<string, typeof allSessions>);
  }, [groupedSessions, chatSearchQuery]);
  
  const handleRenameSession = async (id: string, newTitle: string) => {
    if (!newTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    try {
      await db.update('ai_chat_sessions', 
        { metadata: { title: newTitle.trim() } },
        [{ column: 'id', operator: 'eq', value: id }]
      );
      setEditingSessionId(null);
    } catch {
      setEditingSessionId(null);
    }
  };

  const renderSessionGroup = (groupKey: string, label: string, sessions: GroupedSessions['today']) => {
    if (!sessions?.length) return null;
    return (
      <div key={groupKey} className="space-y-0.5">
        <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground py-1">{label}</div>
        {sessions.map((session) => (
          <SidebarMenuSubItem key={session.id} className="group/session">
            {editingSessionId === session.id ? (
              <div className="flex items-center gap-1 px-1">
                <Input
                  value={editingSessionTitle}
                  onChange={(e) => setEditingSessionTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRenameSession(session.id, editingSessionTitle);
                    }
                    if (e.key === 'Escape') setEditingSessionId(null);
                  }}
                  className="h-6 text-xs flex-1"
                  autoFocus
                />
                <Button variant="ghost" size="icon" className="h-5 w-5"
                  onClick={() => handleRenameSession(session.id, editingSessionTitle)}>
                  <Check className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-5"
                  onClick={() => setEditingSessionId(null)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full pr-2 overflow-hidden">
                <SidebarMenuSubButton
                  onClick={() => handleSelectSession(session.id)}
                  className={cn(
                    "truncate text-xs flex-1 min-w-0 overflow-hidden",
                    sessionId === session.id ? 'bg-sidebar-accent font-medium' : ''
                  )}
                >
                  <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate min-w-0 max-w-[120px]">{session.title || 'New conversation'}</span>
                </SidebarMenuSubButton>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 opacity-0 group-hover/session:opacity-100 focus:opacity-100 transition-opacity shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem onClick={() => {
                      setEditingSessionId(session.id);
                      setEditingSessionTitle(session.title || '');
                    }}>
                      <PenLine className="h-4 w-4 mr-2" />
                      <LCommon id="actions.rename" defaultValue="Rename" />
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={(e) => handleDeleteSession(e as unknown as React.MouseEvent, session.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      <LCommon id="actions.delete" defaultValue="Delete" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </SidebarMenuSubItem>
        ))}
      </div>
    );
  };

  // Guest account button - direct click to smart auth (no dropdown)
  const GuestAccountButton = ({ isCollapsed }: { isCollapsed: boolean }) => {
    const handleAuthClick = () => {
      // Use openExitModal to show embedded auth if active session on /chat
      if (guestExitPrevention?.hasActiveGuestSession) {
        guestExitPrevention.openExitModal();
      } else {
        navigate(getSmartAuthRoute());
      }
    };
    
    if (isCollapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              onClick={handleAuthClick}
              className={cn(
                "w-full justify-center h-10 px-0",
                INTERACTION_STATES.navButton.interactive
              )}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-muted text-muted-foreground">
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <LComponents id="sidebar.account" defaultValue="Account" />
          </TooltipContent>
        </Tooltip>
      );
    }
    
    return (
      <Button 
        variant="ghost" 
        onClick={handleAuthClick}
        className={cn(
          "w-full justify-start gap-3 h-12 px-2",
          INTERACTION_STATES.navButton.interactive
        )}
      >
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-muted text-muted-foreground">
            <User className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-medium truncate">
            <LComponents id="sidebar.account" defaultValue="Account" />
          </p>
        </div>
      </Button>
    );
  };

  // Guest sidebar (chat routes only)
  if (!user) {
    return (
      <Sidebar 
        collapsible="icon"
        className="h-full border-r border-border/50 bg-sidebar/95 backdrop-blur-sm hidden md:flex flex-col transition-all duration-200"
      >
        <SidebarHeader className="px-3 pt-3 pb-2">
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarMenuButton onClick={handleNewChat} className="w-full justify-center px-0 py-2.5">
                  <SquarePen className="h-4 w-4 text-primary" />
                </SidebarMenuButton>
              </TooltipTrigger>
              <TooltipContent side="right"><LCommon id="sidebar.chat_new" defaultValue="New chat" /></TooltipContent>
            </Tooltip>
          ) : (
            <SidebarMenuButton onClick={handleNewChat} className={cn(
              "w-full justify-start gap-2.5 px-3 py-2.5 text-sm font-medium",
              "bg-primary/10 hover:bg-primary/15 text-primary rounded-xl"
            )}>
              <SquarePen className="h-4 w-4" />
              <LComponents id="tax_oracle_sidebar.new_chat" defaultValue="New chat" />
            </SidebarMenuButton>
          )}
        </SidebarHeader>
        <SidebarContent className="flex-1 flex flex-col items-center justify-center px-4">
          {!isCollapsed && (
            <p className={cn(TYPOGRAPHY.labelMuted, 'text-center mb-4')}>
              <LCommon id="sidebar.sign_in_prompt" defaultValue="Sign in to save your chat history" />
            </p>
          )}
        </SidebarContent>
        <SidebarFooter className={cn("py-2 border-t border-border/50 shrink-0 mt-auto", isCollapsed ? "px-2" : "px-3")}>
          {/* Collapse Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className={cn(
                  "shrink-0 text-muted-foreground",
                  INTERACTION_STATES.navButton.interactive,
                  isCollapsed ? "h-8 w-8 mx-auto" : "h-9 w-9 ml-auto"
                )}
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <PanelLeft className={cn("h-4 w-4 shrink-0 transition-transform", isCollapsed && "rotate-180")} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              <span className="flex items-center gap-1.5">
                {isCollapsed ? (
                  <>
                    <LComponents id="sidebar.expand_sidebar" defaultValue="Expand sidebar" />
                    <ArrowRight className="h-3 w-3" />
                  </>
                ) : (
                  <>
                    <LComponents id="sidebar.collapse_sidebar" defaultValue="Collapse sidebar" />
                    <ArrowLeft className="h-3 w-3" />
                  </>
                )}
              </span>
            </TooltipContent>
          </Tooltip>
          
          {/* Divider */}
          <div className={cn("border-t border-border/50 my-1", isCollapsed ? "-mx-2" : "-mx-3")} />
          
          <GuestAccountButton isCollapsed={isCollapsed} />
        </SidebarFooter>
      </Sidebar>
    );
  }

  // Logged-in sidebar
  return (
    <Sidebar 
      collapsible="icon"
      className="h-full border-r border-border/50 bg-sidebar/95 backdrop-blur-sm hidden md:flex flex-col transition-all duration-200"
    >
      {/* Header - minimal */}
      <SidebarHeader className="px-3 pt-3 pb-2 shrink-0" />
      
      <SidebarContent className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {/* Chat Section - Visible on all authenticated routes */}
        <SidebarGroup className="shrink-0 border-b border-border/50 py-2">
            <SidebarGroupContent>
              <SidebarMenu>
                {/* Chat - parent collapsible */}
                {isCollapsed ? (
                  <SidebarMenuItem>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarMenuButton onClick={handleNewChat} className="justify-center">
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        </SidebarMenuButton>
                      </TooltipTrigger>
                      <TooltipContent side="right"><LCommon id="sidebar.chat" defaultValue="Chat" /></TooltipContent>
                    </Tooltip>
                  </SidebarMenuItem>
                ) : (
                  <Collapsible open={openMenus.chat} onOpenChange={() => toggleMenu('chat')}>
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton className="w-full">
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                          <span className="flex-1 text-left"><LCommon id="sidebar.chat" defaultValue="Chat" /></span>
                          {openMenus.chat ? <ChevronDown className={ICONS.sidebarChevron} /> : <ChevronRight className={ICONS.sidebarChevron} />}
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                    </SidebarMenuItem>
                    <CollapsibleContent>
                      <SidebarMenuSub className="pr-0 mr-0">
                        {/* New chat action */}
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton 
                            onClick={handleNewChat}
                            className="text-primary hover:text-primary"
                          >
                            <SquarePen className="h-3.5 w-3.5" />
                            <span><LCommon id="sidebar.chat_new" defaultValue="New chat" /></span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        
                        {/* Projects - nested collapsible */}
                        <SidebarMenuSubItem>
                          <Collapsible open={openMenus.projects} onOpenChange={() => toggleMenu('projects')}>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuSubButton className="w-full">
                                <FolderOpen className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="flex-1 text-left"><LCommon id="sidebar.chat_projects" defaultValue="Projects" /></span>
                                {openMenus.projects ? <ChevronDown className={ICONS.sidebarChevron} /> : <ChevronRight className={ICONS.sidebarChevron} />}
                              </SidebarMenuSubButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="pl-4">
                              {projects.slice(0, 5).map(p => (
                                <SidebarMenuSubItem key={p.id} className="group/project">
                                  {editingProjectId === p.id ? (
                                    <div className="flex items-center gap-1 px-1">
                                      <Input
                                        value={editingProjectName}
                                        onChange={(e) => setEditingProjectName(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            updateProject(p.id, editingProjectName);
                                            setEditingProjectId(null);
                                          }
                                          if (e.key === 'Escape') setEditingProjectId(null);
                                        }}
                                        className="h-6 text-xs flex-1"
                                        autoFocus
                                      />
                                      <Button variant="ghost" size="icon" className="h-5 w-5"
                                        onClick={() => { updateProject(p.id, editingProjectName); setEditingProjectId(null); }}>
                                        <Check className="h-3 w-3" />
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-5 w-5"
                                        onClick={() => setEditingProjectId(null)}>
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-between w-full pr-2">
                                      <SidebarMenuSubButton className="truncate text-xs flex-1">
                                        {p.name}
                                      </SidebarMenuSubButton>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5 opacity-0 group-hover/project:opacity-100 focus:opacity-100 transition-opacity shrink-0"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <MoreHorizontal className="h-3 w-3" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-32">
                                          <DropdownMenuItem onClick={() => {
                                            setEditingProjectId(p.id);
                                            setEditingProjectName(p.name);
                                          }}>
                                          <PenLine className="h-4 w-4 mr-2" />
                                          <LCommon id="actions.rename" defaultValue="Rename" />
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="text-destructive focus:text-destructive"
                                          onClick={() => deleteProject(p.id)}
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          <LCommon id="actions.delete" defaultValue="Delete" />
                                        </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  )}
                                </SidebarMenuSubItem>
                              ))}
                              {!isCreatingProject && (
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton onClick={() => setIsCreatingProject(true)} className="text-xs">
                                    <Plus className="h-3 w-3 mr-1" /> <LCommon id="sidebar.new_project" defaultValue="New project" />
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              )}
                              {isCreatingProject && (
                                <SidebarMenuSubItem>
                                  <div className="flex items-center gap-1 px-1">
                                    <Input ref={newProjectInputRef} value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateProject(); if (e.key === 'Escape') setIsCreatingProject(false); }}
                                      placeholder={lCommon('sidebar.project_name.placeholder', { defaultValue: 'Project name' })} className="h-6 text-xs"
                                    />
                                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={handleCreateProject}><Check className="h-3 w-3" /></Button>
                                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => setIsCreatingProject(false)}><X className="h-3 w-3" /></Button>
                                  </div>
                                </SidebarMenuSubItem>
                              )}
                            </CollapsibleContent>
                          </Collapsible>
                        </SidebarMenuSubItem>
                        
                        {/* History - nested collapsible */}
                        <SidebarMenuSubItem>
                          <Collapsible open={openMenus.history} onOpenChange={() => toggleMenu('history')}>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuSubButton className="w-full">
                                <History className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="flex-1 text-left"><LCommon id="sidebar.history" defaultValue="History" /></span>
                                {openMenus.history ? <ChevronDown className={ICONS.sidebarChevron} /> : <ChevronRight className={ICONS.sidebarChevron} />}
                              </SidebarMenuSubButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="pl-4">
                              <ScrollArea className="max-h-40">
                                {Object.entries(limitedSessions).map(([label, sessions]) => 
                                  renderSessionGroup(label.toLowerCase(), label, sessions)
                                )}
                              </ScrollArea>
                            </CollapsibleContent>
                          </Collapsible>
                        </SidebarMenuSubItem>
                        
                        {/* Search Chat */}
                        <div className="pt-2">
                          <SearchInput
                            value={chatSearchQuery}
                            onChange={(e) => setChatSearchQuery(e.target.value)}
                            placeholder={lCommon('sidebar.search.placeholder', { defaultValue: 'Search...' })}
                            size="compact"
                            containerClassName="w-full"
                          />
                        </div>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        
        {/* HOME Section */}
        <ScrollArea className="flex-1 min-h-0">
          <SidebarGroup className="py-2">
            {!isCollapsed && (
              <SidebarGroupLabel className="px-3 text-[10px] uppercase tracking-wider"><LCommon id="sidebar.home" defaultValue="Home" /></SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {homeMenuItems.map(item => (
                  <SidebarMenuItem key={item.key}>
                    {item.children ? (
                      // Collapsible Overview with children
                      isCollapsed ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton asChild>
                              <NavLink to={item.url} end className={cn(
                                "justify-center",
                                isActive(item.url) && 'bg-sidebar-accent'
                              )}>
                                <item.icon className="h-4 w-4 text-muted-foreground" />
                              </NavLink>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {item.localizationId ? <LCommon id={item.localizationId} defaultValue={item.title} /> : item.title}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Collapsible open={openMenus.overview} onOpenChange={() => toggleMenu('overview')}>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton 
                              className="w-full"
                              onClick={() => navigate(item.url)}
                            >
                              <item.icon className="h-4 w-4 text-muted-foreground" />
                              <span className="flex-1 text-left">
                                {item.localizationId ? <LCommon id={item.localizationId} defaultValue={item.title} /> : item.title}
                              </span>
                              {openMenus.overview ? <ChevronDown className={ICONS.sidebarChevron} /> : <ChevronRight className={ICONS.sidebarChevron} />}
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.children.map(child => (
                                <SidebarMenuSubItem key={child.key}>
                                  <SidebarMenuSubButton asChild>
                                    <NavLink to={child.url} className={cn(
                                      isActive(child.url) && 'bg-sidebar-accent font-medium'
                                    )}>
                                    <child.icon className="h-3.5 w-3.5 text-muted-foreground" />
                                      <span>{child.localizationId ? <LCommon id={child.localizationId} defaultValue={child.title} /> : child.title}</span>
                                    </NavLink>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </Collapsible>
                      )
                    ) : (
                      // Simple items (opportunities, favorites, help)
                      isCollapsed ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton asChild>
                              <NavLink to={item.url} end={item.url === ROUTES.ACCOUNT} className={cn(
                                "justify-center",
                                isActive(item.url) && 'bg-sidebar-accent'
                              )}>
                                <item.icon className="h-4 w-4 text-muted-foreground" />
                              </NavLink>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {item.localizationId ? <LCommon id={item.localizationId} defaultValue={item.title} /> : item.title}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <SidebarMenuButton asChild>
                          <NavLink to={item.url} end={item.url === ROUTES.ACCOUNT} className={cn(
                            isActive(item.url) && 'bg-sidebar-accent font-medium'
                          )}>
                            <item.icon className="h-4 w-4 text-muted-foreground" />
                            <span className="flex-1">
                              {item.localizationId ? <LCommon id={item.localizationId} defaultValue={item.title} /> : item.title}
                            </span>
                            {item.badge === 'progress' && completionPercentage < 100 && (
                              <CircularProgress value={completionPercentage} size="sm" />
                            )}
                            {item.badge === 'count' && unreadCount > 0 && (
                              <Badge variant="destructive" className="h-5 min-w-5 px-1 text-xs">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </Badge>
                            )}
                          </NavLink>
                        </SidebarMenuButton>
                      )
                    )}
                  </SidebarMenuItem>
                ))}
                
                {/* Content collapsible - role gated */}
                {canSeeContent && (
                  <SidebarMenuItem>
                    {isCollapsed ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarMenuButton asChild>
                            <NavLink to={ROUTES.ACCOUNT_PUBLICATIONS} className="justify-center">
                              <PenSquare className="h-4 w-4 text-muted-foreground" />
                            </NavLink>
                          </SidebarMenuButton>
                        </TooltipTrigger>
                        <TooltipContent side="right"><LCommon id="sidebar.publications" defaultValue="Publications" /></TooltipContent>
                      </Tooltip>
                    ) : (
                      <Collapsible open={openMenus.content} onOpenChange={() => toggleMenu('content')}>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton className="w-full">
                            <PenSquare className="h-4 w-4 text-muted-foreground" />
                            <span className="flex-1 text-left"><LCommon id="sidebar.publications" defaultValue="Publications" /></span>
                            {openMenus.content ? <ChevronDown className={ICONS.sidebarChevron} /> : <ChevronRight className={ICONS.sidebarChevron} />}
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {contentMenuItems.filter(item => {
                              if (item.key === 'new_publication') return canAccessPublications;
                              if (item.key === 'publications') return canAccessPublications;
                              if (item.key === 'comments') return canAccessComments;
                              if (item.key === 'media') return canAccessMedia;
                              return false;
                            }).map(item => (
                              <SidebarMenuSubItem key={item.key}>
                                <SidebarMenuSubButton asChild>
                                  <NavLink to={item.url} className={cn(
                                    isActive(item.url) && 'bg-sidebar-accent font-medium'
                                  )}>
                                    <item.icon className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span>{item.localizationId ? <LCommon id={item.localizationId} defaultValue={item.title} /> : item.title}</span>
                                  </NavLink>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          
          {/* ADMIN Section - collapsed by default, role-gated */}
          {canSeeAdmin && (
            <SidebarGroup className="py-2 border-t border-border/50">
              {isCollapsed ? (
                <SidebarGroupContent>
                  <SidebarMenu>
                    {adminMenuItems.filter(item => {
                      if (item.key === 'operations') return canAccessOperations;
                      if (item.key === 'intelligence') return canAccessIntelligence;
                      if (item.key === 'management') return canAccessSettings;
                      return false;
                    }).map(item => (
                      <SidebarMenuItem key={item.key}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton asChild>
                              <NavLink to={item.url} className={cn(
                                "justify-center",
                                isActive(item.url) && 'bg-sidebar-accent'
                              )}>
                                <item.icon className="h-4 w-4 text-muted-foreground" />
                              </NavLink>
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          <TooltipContent side="right">{item.title}</TooltipContent>
                        </Tooltip>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              ) : (
                <Collapsible open={openMenus.admin} onOpenChange={() => toggleMenu('admin')}>
                  <CollapsibleTrigger asChild>
                    <SidebarGroupLabel className="text-[10px] uppercase tracking-wider cursor-pointer flex items-center justify-between w-full">
                      <span><LCommon id="sidebar.admin" defaultValue="Admin" /></span>
                      {openMenus.admin ? <ChevronDown className={ICONS.sidebarChevron} /> : <ChevronRight className={ICONS.sidebarChevron} />}
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {adminMenuItems.filter(item => {
                          if (item.key === 'operations') return canAccessOperations;
                          if (item.key === 'intelligence') return canAccessIntelligence;
                          if (item.key === 'management') return canAccessSettings;
                          return false;
                        }).map(item => (
                          <SidebarMenuItem key={item.key}>
                            <SidebarMenuButton asChild>
                              <NavLink to={item.url} className={cn(
                                RESPONSIVE.flexRowCenterGap3,
                                isActive(item.url) && 'bg-sidebar-accent font-medium'
                              )}>
                                <item.icon className="h-4 w-4 text-muted-foreground" />
                                <span>{item.title}</span>
                              </NavLink>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </SidebarGroup>
          )}
        </ScrollArea>
      </SidebarContent>
      
      {/* ACCOUNT Footer - Collapse button + Slack style dropdown */}
      <SidebarFooter className={cn("py-2 border-t border-border/50 shrink-0 mt-auto", isCollapsed ? "px-2" : "px-3")}>
        {/* Collapse Sidebar Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className={cn(
                "shrink-0 text-muted-foreground",
                INTERACTION_STATES.navButton.interactive,
                isCollapsed ? "h-8 w-8 mx-auto" : "h-9 w-9 ml-auto"
              )}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <PanelLeft className={cn("h-4 w-4 shrink-0 transition-transform", isCollapsed && "rotate-180")} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <span className="flex items-center gap-1.5">
              {isCollapsed ? (
                <>
                  <LComponents id="sidebar.expand_sidebar" defaultValue="Expand sidebar" />
                  <ArrowRight className="h-3 w-3" />
                </>
              ) : (
                <>
                  <LComponents id="sidebar.collapse_sidebar" defaultValue="Collapse sidebar" />
                  <ArrowLeft className="h-3 w-3" />
                </>
              )}
            </span>
          </TooltipContent>
        </Tooltip>
        
        {/* Divider - matches SidebarFooter top border pattern */}
        <div className={cn("border-t border-border/50 my-1", isCollapsed ? "-mx-2" : "-mx-3")} />
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className={cn(
              "w-full justify-start gap-3 h-12 px-2",
              isCollapsed && "justify-center px-0",
              INTERACTION_STATES.navButton.interactive,
              "data-[state=open]:bg-muted/50 data-[state=open]:text-foreground"
            )}>
              <Avatar className="h-8 w-8">
                <AvatarImage src={avatarUrl} />
                <AvatarFallback className="bg-muted text-muted-foreground text-xs">{userInitials}</AvatarFallback>
              </Avatar>
              {!isCollapsed && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium truncate">{displayName || user?.email?.split('@')[0]}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuItem onClick={() => navigate(getPath('help'))} className="whitespace-nowrap">
              <Headphones className="h-4 w-4 mr-2" /> <LCommon id="nav.help_center" defaultValue="Help Center" />
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            {/* Sign Out */}
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive whitespace-nowrap">
              <LogOut className="h-4 w-4 mr-2" /> <LCommon id="actions.sign_out" defaultValue="Sign Out" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
